import { formatMskDateTime } from '../../modules/matching/routes/Dashboard/core/mskTime'
import {
  ClientRiskLevel,
  ClientSnapshot,
  LEVEL_TITLES,
  PATTERN_LABELS,
  SCORE_SCALE,
} from './core/_models'

export type ClientSnapshotState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; snapshot: ClientSnapshot | null }

const LEVEL_COLOR: Record<ClientRiskLevel, string> = {
  alarm: 'var(--bs-danger)',
  look: 'var(--bs-warning)',
  observe: 'var(--bs-gray-600)',
}

const GOOD_COLOR = 'var(--bs-success)'

const formatDuration = (seconds: number | null): string => {
  if (seconds === null) {
    return '—'
  }
  if (seconds < 60) {
    return `${seconds} с`
  }
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) {
    return `${minutes} мин`
  }
  const rest = minutes % 60
  return rest === 0 ? `${Math.floor(minutes / 60)} ч` : `${Math.floor(minutes / 60)} ч ${rest} мин`
}

const formatPoints = (points: number): string => (points > 0 ? `+${points}` : points < 0 ? `−${-points}` : '0')

const formatDate = (raw: string | null): string => (raw ? formatMskDateTime(raw).split(',')[0] : '—')

const cardStyle = {
  backgroundColor: 'var(--bs-body-bg)',
  color: 'var(--bs-body-color)',
  border: '1px solid var(--bs-border-color)',
  borderRadius: 8,
  boxShadow: '0 4px 16px rgba(0,0,0,.18)',
  padding: '12px 14px',
  width: 460,
  fontSize: 12,
}

const sectionTitle = {
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: '.04em',
  textTransform: 'uppercase' as const,
  color: 'var(--bs-secondary-color)',
  margin: '10px 0 4px',
}

/** Шкала балла: зоны «надёжный / наблюдение / присмотреться / тревога» и метка текущего балла. */
const ScoreBar = ({ score, color }: { score: number; color: string }) => {
  const { min, max, lookFrom, alarmFrom } = SCORE_SCALE
  const span = max - min
  const at = (value: number) => ((Math.min(Math.max(value, min), max) - min) / span) * 100
  const zones = [
    { from: min, to: 0, color: GOOD_COLOR },
    { from: 0, to: lookFrom, color: 'var(--bs-gray-400)' },
    { from: lookFrom, to: alarmFrom, color: 'var(--bs-warning)' },
    { from: alarmFrom, to: max, color: 'var(--bs-danger)' },
  ]

  return (
    <div data-testid='client-score-bar' style={{ position: 'relative', height: 18, marginTop: 8 }}>
      <div style={{ position: 'absolute', top: 6, left: 0, right: 0, height: 6, display: 'flex', borderRadius: 3, overflow: 'hidden' }}>
        {zones.map((zone) => (
          <div key={zone.from} style={{ width: `${((zone.to - zone.from) / span) * 100}%`, backgroundColor: zone.color, opacity: 0.45 }} />
        ))}
      </div>
      <div
        style={{
          position: 'absolute',
          left: `calc(${at(score)}% - 6px)`,
          top: 3,
          width: 12,
          height: 12,
          borderRadius: '50%',
          backgroundColor: color,
          border: '2px solid var(--bs-body-bg)',
          boxShadow: '0 0 0 1px ' + color,
        }}
      />
    </div>
  )
}

const Stat = ({ label, value, hint }: { label: string; value: string | number; hint?: string }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '2px 0' }}>
    <span style={{ color: 'var(--bs-secondary-color)' }}>{label}</span>
    <span style={{ fontWeight: 600, textAlign: 'right' }}>
      {value}
      {hint && <span style={{ fontWeight: 400, color: 'var(--bs-secondary-color)' }}> {hint}</span>}
    </span>
  </div>
)

/** Депозиты за 180 дней одной полосой: успешные, неудачные и с поддельным чеком. */
const DepositsBar = ({ completed, failed, forged }: { completed: number; failed: number; forged: number }) => {
  const total = completed + failed
  if (total === 0) {
    return null
  }
  const parts = [
    { value: Math.max(completed - forged, 0), color: GOOD_COLOR },
    { value: forged, color: 'var(--bs-danger)' },
    { value: failed, color: 'var(--bs-gray-400)' },
  ]

  return (
    <div style={{ display: 'flex', height: 6, borderRadius: 3, overflow: 'hidden', margin: '2px 0 4px' }}>
      {parts.map((part, index) =>
        part.value > 0 ? <div key={index} style={{ flexGrow: part.value, backgroundColor: part.color }} /> : null
      )}
    </div>
  )
}

export const ClientSnapshotCard = ({ state }: { state: ClientSnapshotState }) => {
  if (state.status === 'loading') {
    return (
      <div style={cardStyle} className='d-flex align-items-center gap-2'>
        <span className='spinner-border spinner-border-sm' role='status' />
        <span>Загружаем снимок клиента…</span>
      </div>
    )
  }

  if (state.status === 'error') {
    return <div style={cardStyle}>Не удалось загрузить снимок клиента</div>
  }

  if (state.snapshot === null) {
    return (
      <div style={cardStyle}>
        <div style={{ fontWeight: 600 }}>Клиент ещё не собран</div>
        <div style={{ color: 'var(--bs-secondary-color)', marginTop: 4 }}>
          У учётки пока нет идентификаторов или профиль ещё не посчитан.
        </div>
      </div>
    )
  }

  const { profile, risk } = state.snapshot
  const good = risk.level === 'observe' && risk.score < 0
  const color = good ? GOOD_COLOR : LEVEL_COLOR[risk.level]
  const patterns = [...risk.patterns].sort((a, b) => Number(b.triggered) - Number(a.triggered))

  return (
    <div style={cardStyle}>
      <div className='d-flex align-items-center justify-content-between gap-2'>
        <span
          style={{
            backgroundColor: color,
            color: risk.level === 'look' ? '#212121' : '#fff',
            borderRadius: 4,
            padding: '2px 8px',
            fontWeight: 600,
          }}
        >
          {good ? 'Надёжный' : LEVEL_TITLES[risk.level]}
        </span>
        <span style={{ fontWeight: 600 }}>{risk.summary}</span>
      </div>
      <ScoreBar score={risk.score} color={color} />

      <div style={sectionTitle}>Оценка</div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <tbody>
          {patterns.map((pattern) => (
            <tr
              key={pattern.code}
              style={{
                borderBottom: '1px solid var(--bs-border-color)',
                color: pattern.triggered ? 'var(--bs-body-color)' : 'var(--bs-secondary-color)',
              }}
            >
              <td style={{ padding: '3px 6px 3px 0', whiteSpace: 'nowrap' }}>{PATTERN_LABELS[pattern.code] ?? pattern.code}</td>
              <td
                style={{
                  padding: '3px 6px',
                  textAlign: 'right',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  color: pattern.triggered ? (pattern.points < 0 ? GOOD_COLOR : 'var(--bs-danger)') : undefined,
                }}
              >
                {formatPoints(pattern.points)}
              </td>
              <td style={{ padding: '3px 0 3px 6px' }}>{pattern.triggered ? pattern.subject : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className='d-flex gap-4'>
        <div style={{ flex: 1 }}>
          <div style={sectionTitle}>История за 180 дней</div>
          <DepositsBar completed={profile.depositsCompleted} failed={profile.depositsFailed} forged={profile.receiptsForged} />
          <Stat label='Депозиты успешные' value={profile.depositsCompleted} />
          <Stat label='неудачные' value={profile.depositsFailed} />
          <Stat label='с поддельным чеком' value={profile.receiptsForged} />
          <Stat label='Диспуты' value={profile.disputesOpened} hint={`проиграно ${profile.disputesLost}`} />
          <Stat label='Выплаты' value={profile.payoutsCompleted} hint={`неудачных ${profile.payoutsFailed}`} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={sectionTitle}>Кто это</div>
          <Stat label='Учётки' value={profile.accounts} hint={`у ${profile.merchants} мерч.`} />
          <Stat label='Реквизиты' value={profile.requisites} />
          <Stat label='Общий реквизит' value={profile.sharedRequisiteClients} hint='клиентов' />
          <Stat label='Браузеры / устройства' value={`${profile.browsers} / ${profile.devices}`} />
          <Stat label='Связи с заблокир.' value={profile.blockedLinks} />
        </div>
      </div>

      <div style={sectionTitle}>Привычки</div>
      <Stat label='Обычный банк' value={profile.usualSenderBank ?? '—'} />
      <Stat label='Обычный метод' value={profile.usualPaymentMethod ?? '—'} />
      <Stat label='Обычное время оплаты' value={formatDuration(profile.medianSecondsToPay)} />
      <Stat label='Впервые / последний раз' value={`${formatDate(profile.firstSeenAt)} / ${formatDate(profile.lastSeenAt)}`} />

      {profile.profileRefreshedAt && (
        <div style={{ marginTop: 8, fontSize: 10, color: 'var(--bs-secondary-color)' }}>
          Профиль обновлён {formatMskDateTime(profile.profileRefreshedAt)} (МСК), пересчитывается раз в час
        </div>
      )}
    </div>
  )
}
